import React, { useState } from 'react';
import { Avatar, ElevatedButton } from '@producer/ui-kit';
import { observer } from 'mobx-react-lite';
import { BadgeIcon, MixerHorizontalIcon, PersonIcon, PieChartIcon } from '@radix-ui/react-icons';
import styled from 'styled-components';

import { Task, TaskStatus } from '../../../../app/entities';
import { CheckboxItem } from '../../../../components/modals/dropdown-menu/DropdownMenu';
import { useCurrentSpace } from '../../../../hooks/use-current-space';
import { FlagIcon, ProjectIcon2 } from '../../../../components/icons';
import { DrilldownMenu } from '../../../../components/drilldown-menu/drilldown-menu';
import { Dot } from '../../../process/components/step-status-selector';
import { PriorityIcon } from '../../../process/components/priority-selector';
import { Project, ProjectPriority } from '../../../../app/entities';
import { SpaceFilterChip } from './space-filter-chip';
import { CustomAvatar } from '../styled-tasks-components';
import { Point } from '../../../tags/components/styled-tags-components';

type FiltersProps = {
  projectIds?: string[];
  status?: TaskStatus[];
  assigneeIds?: string[];
  tagIds?: string[];
  priority?: ProjectPriority[];
};

export const SpaceTasksFilters: React.FC = observer(() => {
  const space = useCurrentSpace();
  const spaceProjects = space?.projects;
  const spaceMembers = space.members;
  const spaceTags = space.tags;
  const spaceTasksFilter = space.tasksFilter;
  const currentTokens = spaceTasksFilter.orderedTokens;

  const [alignOffset, setAlignOffset] = useState(0);
  const [defaultMenuPath, setDefaultMenuPath] = useState<number[]>([]);

  const handleCheckSelect = (
    event: Event,
    prop: keyof Pick<
      typeof spaceTasksFilter,
      'projectIds' | 'status' | 'priority' | 'assigneeIds' | 'tagIds'
    >,
  ) => {
    const target = event.currentTarget as HTMLElement;
    const isChecked = target.getAttribute('data-state') === 'checked';

    if (spaceTasksFilter[prop]?.length === 1 && isChecked) {
      if (spaceTasksFilter.filtersCount === 1) {
        setDefaultMenuPath([]);
        event.preventDefault();
      }
      return;
    }
    event.preventDefault();
  };

  const sortedProjectsItems: CheckboxItem[] = [...spaceProjects]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((project) => {
      const isChecked = spaceTasksFilter.projectIds?.includes(project._id);

      return {
        title: project.name,
        onCheckedChange: (isChecked) => {
          if (isChecked) {
            spaceTasksFilter.addToFilter('projectIds', project._id);
          } else {
            spaceTasksFilter.removeFromFilter('projectIds', project._id);
          }
        },
        onSelect: (e) => handleCheckSelect(e, 'projectIds'),
        isChecked,
      };
    });

  const projectItems: CheckboxItem[] = [
    {
      title: 'No Project',
      onCheckedChange: (isChecked) => {
        if (isChecked) {
          spaceTasksFilter.addToFilter('projectIds', ['', null, undefined]);
        } else {
          spaceTasksFilter.removeFromFilter('projectIds', ['', null, undefined]);
        }
      },
      onSelect: (e) => handleCheckSelect(e, 'projectIds'),
      isChecked: spaceTasksFilter.projectIds?.includes(''),
    },
    ...sortedProjectsItems,
  ];

  const statusItems: CheckboxItem[] = Object.entries(Task.StatusMapper).map(([key, { title }]) => {
    const isChecked = spaceTasksFilter.status?.includes(key as TaskStatus);

    return {
      title: title,
      onCheckedChange: (isChecked) => {
        if (isChecked) {
          spaceTasksFilter.addToFilter('status', key);
        } else {
          spaceTasksFilter.removeFromFilter('status', key);
        }
      },
      onSelect: (e) => handleCheckSelect(e, 'status'),
      isChecked,
      icon: <Dot data-status={key as TaskStatus} />,
    };
  });

  const sortedMembersItems: CheckboxItem[] = [...spaceMembers]
    .sort((a, b) => a.fullName.localeCompare(b.fullName))
    .map((el) => {
      const isChecked = spaceTasksFilter.assigneeIds?.includes(el._id);

      return {
        title: el.fullName,
        value: el._id,
        icon: <Avatar size="small" initials={el.initials} src={el.avatar} />,
        onCheckedChange: (isChecked) => {
          if (isChecked) {
            spaceTasksFilter.addToFilter('assigneeIds', el._id);
          } else {
            spaceTasksFilter.removeFromFilter('assigneeIds', el._id);
          }
        },
        onSelect: (e) => handleCheckSelect(e, 'assigneeIds'),
        isChecked,
      };
    });

  const assigneeItems: CheckboxItem[] = [
    {
      title: 'No assignee',
      icon: (
        <CustomAvatar>
          <PersonIcon />
        </CustomAvatar>
      ),
      onCheckedChange: (isChecked) => {
        if (isChecked) {
          spaceTasksFilter.addToFilter('assigneeIds', ['', null, undefined]);
        } else {
          spaceTasksFilter.removeFromFilter('assigneeIds', ['', null, undefined]);
        }
      },
      onSelect: (e) => handleCheckSelect(e, 'assigneeIds'),
      isChecked: spaceTasksFilter.assigneeIds?.includes(''),
    },
    ...sortedMembersItems,
  ];

  const sortedTagsItems: CheckboxItem[] = spaceTags
    ?.filter((tag) => tag.target === 'task')
    ?.map((tag) => {
      const isChecked = spaceTasksFilter.tagIds?.includes(tag._id);

      return {
        title: tag.text,
        value: tag._id,
        icon: <Point color={tag.color} />,
        onCheckedChange: (isChecked) => {
          if (isChecked) {
            spaceTasksFilter.addToFilter('tagIds', tag._id);
          } else {
            spaceTasksFilter.removeFromFilter('tagIds', tag._id);
          }
        },
        onSelect: (e) => handleCheckSelect(e, 'tagIds'),
        isChecked,
      };
    });

  const tagItems: CheckboxItem[] = [
    {
      title: 'No tag',
      icon: <Point color="defaultColor" />,
      onCheckedChange: (isChecked) => {
        if (isChecked) {
          spaceTasksFilter.addToFilter('tagIds', ['', null, undefined]);
        } else {
          spaceTasksFilter.removeFromFilter('tagIds', ['', null, undefined]);
        }
      },
      onSelect: (e) => handleCheckSelect(e, 'tagIds'),
      isChecked: spaceTasksFilter.tagIds?.includes(''),
    },
    ...sortedTagsItems,
  ];

  const priorityItems: CheckboxItem[] = Project.PriorityArray.map(([key, { title }]) => {
    const isChecked = spaceTasksFilter.priority?.includes(Number(key));

    return {
      title: title,
      icon: <PriorityIcon priority={Number(key)} />,
      onCheckedChange: (isChecked) => {
        if (isChecked) {
          spaceTasksFilter.addToFilter('priority', Number(key));
        } else {
          spaceTasksFilter.removeFromFilter('priority', Number(key));
        }
      },
      onSelect: (e) => handleCheckSelect(e, 'priority'),
      isChecked: isChecked,
    };
  });

  const options: React.ComponentProps<typeof DrilldownMenu>['items'] = [
    {
      menuKey: 'project',
      title: 'Project',
      icon: <ProjectIcon2 />,
      subItems: projectItems,
      onSelect: (e) => {
        e.preventDefault();
      },
    },
    {
      menuKey: 'status',
      title: 'Status',
      icon: <PieChartIcon />,
      subItems: statusItems,
      onSelect: (e) => {
        e.preventDefault();
      },
    },
    {
      menuKey: 'assignee',
      title: 'Assignee',
      icon: <PersonIcon />,
      subItems: assigneeItems,
      onSelect: (e) => {
        e.preventDefault();
      },
    },
    {
      menuKey: 'priority',
      title: 'Priority',
      icon: <FlagIcon />,
      subItems: priorityItems,
      onSelect: (e) => {
        e.preventDefault();
      },
    },
    {
      menuKey: 'tag',
      title: 'Tag',
      icon: <BadgeIcon />,
      subItems: tagItems,
      onSelect: (e) => {
        e.preventDefault();
      },
    },
  ];

  const presetFilterTrigger = (event: React.MouseEvent<HTMLDivElement>, token?: string) => {
    const div = event.currentTarget;
    const offsetLeft = div.offsetLeft;
    const offset = Math.ceil(offsetLeft - 80);

    setAlignOffset(offset);

    if (!token) {
      setDefaultMenuPath([]);
      return;
    }

    const menuPath = options.findIndex((option) => option.menuKey === token);
    setDefaultMenuPath([menuPath]);
  };

  return (
    <DrilldownMenu
      items={options}
      align="start"
      alignOffset={alignOffset}
      defaultMenuPath={defaultMenuPath}
      trigger={
        <Container>
          {currentTokens?.map((token) => {
            const selectedProp = spaceTasksFilter.propsMapper[
              token as keyof typeof spaceTasksFilter.propsMapper
            ] as keyof FiltersProps;

            return (
              <div
                key={token}
                onPointerEnter={(e) => presetFilterTrigger(e, token)}
                onClick={(e) => presetFilterTrigger(e, token)}
              >
                <SpaceFilterChip space={space} selectedProp={selectedProp} />
              </div>
            );
          })}

          <div
            onPointerEnter={(e) => presetFilterTrigger(e)}
            onClick={(e) => presetFilterTrigger(e)}
          >
            <ElevatedButton
              icon={<MixerHorizontalIcon />}
              text={spaceTasksFilter.filtersCount ? '' : 'Filter'}
            />
          </div>
        </Container>
      }
      modal
    />
  );
});

const Container = styled.div`
  display: flex;
  gap: 0.8rem;
`;
